import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Archive, Mail, Trash2, Settings, LogOut, Menu, X,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV_GROUPS = [
  {
    title: "Principal",
    items: [
      { id: "dashboard", label: "Tableau de bord", icon: LayoutDashboard, to: "/enseignant" },
      { id: "archives", label: "Classes archivées", icon: Archive, to: "/enseignant/archives" },
    ],
  },
  {
    title: "Outils",
    items: [
      { id: "email", label: "Modèle d'e-mail", icon: Mail, to: "/enseignant/modele-email" },
    ],
  },
  {
    title: "Gestion",
    items: [
      { id: "corbeille", label: "Corbeille", icon: Trash2, to: "/enseignant/corbeille" },
      { id: "parametres", label: "Paramètres", icon: Settings, to: "/enseignant/parametres" },
    ],
  },
];

const roleLabel = (role) => (role === "teacher" ? "Enseignant" : role === "super_admin" ? "Administrateur" : "Parent");

// Coquille de navigation permanente de l'espace enseignant : barre latérale (desktop) + tiroir (mobile).
export function TeacherShell({ children, active = "dashboard", contentClassName }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const go = (item) => { setOpen(false); navigate(item.to); };

  const NavList = ({ onNav }) => (
    <nav className="flex-1 space-y-5 px-3 py-2">
      {NAV_GROUPS.map((group) => (
        <div key={group.title}>
          <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{group.title}</p>
          <div className="space-y-1">
            {group.items.map((item) => {
              const isActive = active === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onNav(item)}
                  data-testid={`teacher-nav-${item.id}`}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-secondary text-blue-900 dark:bg-blue-500/15 dark:text-blue-300"
                      : "text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white"
                  }`}
                >
                  <item.icon size={18} strokeWidth={isActive ? 2.2 : 1.9} /> {item.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );

  const Brand = (
    <div className="flex items-center gap-3 px-5 py-6">
      <Logo size={40} />
      <div className="leading-tight">
        <p className="font-heading text-base font-semibold tracking-tight text-slate-900 dark:text-white">Le Passeport</p>
        <p className="text-xs text-slate-400">Suivi hebdomadaire</p>
      </div>
    </div>
  );

  const UserFooter = (
    <div className="border-t border-slate-100 px-4 py-4 dark:border-white/5">
      <div className="mb-3 flex items-center gap-3 px-1">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-blue-900 dark:bg-blue-500/15 dark:text-blue-300">
          {(user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")}
        </div>
        <div className="min-w-0 leading-tight">
          <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{user?.first_name} {user?.last_name}</p>
          <p className="text-xs text-slate-400">{roleLabel(user?.role)}</p>
        </div>
      </div>
      <button
        onClick={logout}
        data-testid="logout-button"
        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white"
      >
        <LogOut size={18} strokeWidth={1.9} /> Se déconnecter
      </button>
    </div>
  );

  return (
    <div className="min-h-screen w-full bg-background text-foreground">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-slate-100 bg-white dark:border-white/5 dark:bg-card md:flex">
        {Brand}
        <NavList onNav={go} />
        {UserFooter}
      </aside>

      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-100 bg-white/80 px-4 py-3 backdrop-blur-xl dark:border-white/5 dark:bg-card/80 md:hidden">
        <div className="flex items-center gap-2.5">
          <Logo size={32} />
          <span className="font-heading text-sm font-semibold tracking-tight text-slate-900 dark:text-white">Le Passeport</span>
        </div>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-open" className="rounded-lg p-2 text-slate-600 dark:text-slate-300" aria-label="Ouvrir le menu">
          <Menu size={22} />
        </button>
      </header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)} className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm md:hidden" />
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "tween", duration: 0.25 }}
              className="fixed inset-y-0 left-0 z-50 flex w-72 flex-col overflow-y-auto bg-white dark:bg-card md:hidden" data-testid="mobile-drawer"
            >
              <div className="flex items-center justify-between pr-3">
                {Brand}
                <button onClick={() => setOpen(false)} className="rounded-lg p-2 text-slate-500" aria-label="Fermer"><X size={20} /></button>
              </div>
              <NavList onNav={go} />
              {UserFooter}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <main className="md:pl-64">
        <div className={contentClassName || "mx-auto max-w-6xl px-4 py-8 sm:px-8 sm:py-10"}>{children}</div>
      </main>
    </div>
  );
}
